import { ChangeEvent, RefObject, useEffect, useRef, useState } from 'react';
import { createErrorToast, createSuccessToast } from '../notification/ToastService';

interface AvatarMessages {
  avatarInvalidFile: string;
  avatarTooLarge: string;
  avatarUploadSuccess: string;
  avatarUploadError: string;
  avatarRemoveSuccess: string;
  avatarRemoveError: string;
}

interface UseAvatarEditParams {
  avatarUrl?: string | null;
  uploadAvatar: (file: File) => Promise<unknown>;
  removeAvatar: () => Promise<unknown>;
  notifyAvatarUpdated?: () => void;
  messages: AvatarMessages;
  fileInputRef: RefObject<HTMLInputElement | null>;
  canUploadOrRemove?: () => boolean;
  onUploadSuccess?: () => void;
  onRemoveSuccess?: () => void;
}

interface UseAvatarEditResult {
  avatarUrl?: string;
  uploadingAvatar: boolean;
  removingAvatar: boolean;
  handleAvatarSelected: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleAvatarRemoved: () => Promise<void>;
}

export const useAvatarEdit = ({
  avatarUrl: initialAvatarUrl,
  uploadAvatar,
  removeAvatar,
  notifyAvatarUpdated,
  messages,
  fileInputRef,
  canUploadOrRemove,
  onUploadSuccess,
  onRemoveSuccess
}: UseAvatarEditParams): UseAvatarEditResult => {
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(initialAvatarUrl ?? undefined);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [removingAvatar, setRemovingAvatar] = useState(false);

  const previewObjectUrlRef = useRef<string | undefined>(undefined);

  const maxAvatarFileBytes = 2 * 1024 * 1024;
  const allowedAvatarContentTypes = ['image/jpeg', 'image/png'];

  useEffect(() => {
    setAvatarUrl(initialAvatarUrl ?? undefined);
  }, [initialAvatarUrl]);

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
      }
    };
  }, []);

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAvatarSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    clearFileInput();

    if (!selectedFile) {
      return;
    }

    if (!allowedAvatarContentTypes.includes(selectedFile.type)) {
      createErrorToast(messages.avatarInvalidFile);
      return;
    }

    if (selectedFile.size > maxAvatarFileBytes) {
      createErrorToast(messages.avatarTooLarge);
      return;
    }

    if (canUploadOrRemove && !canUploadOrRemove()) {
      createErrorToast(messages.avatarUploadError);
      return;
    }

    const previewUrl = URL.createObjectURL(selectedFile);

    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
    }

    previewObjectUrlRef.current = previewUrl;
    setAvatarUrl(previewUrl);
    setUploadingAvatar(true);

    try {
      await uploadAvatar(selectedFile);
      createSuccessToast(messages.avatarUploadSuccess);
      onUploadSuccess?.();
      notifyAvatarUpdated?.();
    } catch {
      setAvatarUrl(initialAvatarUrl ?? undefined);
      createErrorToast(messages.avatarUploadError);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarRemoved = async () => {
    if (canUploadOrRemove && !canUploadOrRemove()) {
      createErrorToast(messages.avatarRemoveError);
      return;
    }

    setRemovingAvatar(true);

    try {
      await removeAvatar();

      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = undefined;
      }

      setAvatarUrl(undefined);
      createSuccessToast(messages.avatarRemoveSuccess);
      onRemoveSuccess?.();
      notifyAvatarUpdated?.();
    } catch {
      createErrorToast(messages.avatarRemoveError);
    } finally {
      setRemovingAvatar(false);
    }
  };

  return {
    avatarUrl,
    uploadingAvatar,
    removingAvatar,
    handleAvatarSelected,
    handleAvatarRemoved
  };
};